import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Users, Check, X, ChevronDown, ChevronUp } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { PROJECTS_URL, PROJECT_MEMBERS_URL, PROJECT_ASSIGNMENT_CONFIG_URL, USERS_SIMPLE_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { Select } from "antd";

export default function ProjectsManager() {
    const { fetch, create, update, remove } = useApiMethods();

    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);

    // Add project
    const [newName, setNewName] = useState("");
    const [newDesc, setNewDesc] = useState("");
    const [adding, setAdding] = useState(false);

    // Edit project
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState("");
    const [editDesc, setEditDesc] = useState("");

    // Members panel
    const [expandedProject, setExpandedProject] = useState(null);
    const [members, setMembers] = useState({});          // { project_id: [...] }
    const [addUserId, setAddUserId] = useState(null);
    const [addRole, setAddRole] = useState('member');

    // Organisation-wide: may a lead's project be picked on the customer form
    const [assignment, setAssignment] = useState(null);   // { is_enabled, can_edit, unassigned_count }
    const [savingAssignment, setSavingAssignment] = useState(false);

    const loadProjects = async () => {
        const res = await fetch(PROJECTS_URL, {}, setLoading);
        if (res?.status === 'success') setProjects(res.data || []);
    };

    const notifyProjectsChanged = () => window.dispatchEvent(new Event('projects-changed'));

    const loadUsers = async () => {
        const res = await fetch(USERS_SIMPLE_URL);
        if (res?.status === 'success') setUsers(res.data || []);
    };

    const loadAssignment = async () => {
        const res = await fetch(PROJECT_ASSIGNMENT_CONFIG_URL);
        if (res?.status === 'success') setAssignment(res);
    };

    useEffect(() => { loadProjects(); loadUsers(); loadAssignment(); }, []);

    const handleToggleAssignment = async () => {
        const next = !assignment?.is_enabled;
        const res = await update(PROJECT_ASSIGNMENT_CONFIG_URL, { is_enabled: next }, false, setSavingAssignment);
        if (res?.status === 'success') {
            setAssignment(res);
            ToastMsg("success", res.message);
        } else {
            ToastMsg("error", res?.message || "Failed to update");
        }
    };

    const loadMembers = async (projectId) => {
        const res = await fetch(`${PROJECT_MEMBERS_URL}?project_id=${projectId}`);
        if (res?.status === 'success') {
            setMembers(prev => ({ ...prev, [projectId]: res.data || [] }));
        }
    };

    const toggleExpand = (projectId) => {
        if (expandedProject === projectId) {
            setExpandedProject(null);
        } else {
            setExpandedProject(projectId);
            loadMembers(projectId);
            setAddUserId(null);
            setAddRole('member');
        }
    };

    // Project CRUD
    const handleAdd = async () => {
        const name = newName.trim();
        if (!name) return;
        const res = await create(PROJECTS_URL, { name, description: newDesc.trim() }, false, setAdding);
        if (res?.status === 'success') {
            ToastMsg("Project created", "success");
            setNewName(""); setNewDesc("");
            loadProjects();
            notifyProjectsChanged();
        } else {
            ToastMsg(res?.message || "Failed to create", "error");
        }
    };

    const handleUpdate = async (id) => {
        const name = editName.trim();
        if (!name) return;
        const res = await update(PROJECTS_URL, { id, name, description: editDesc }, false, null);
        if (res?.status === 'success') {
            ToastMsg("Updated", "success");
            setEditId(null);
            loadProjects();
            notifyProjectsChanged();
        } else {
            ToastMsg(res?.message || "Failed to update", "error");
        }
    };

    const handleToggleActive = async (project) => {
        const res = await update(PROJECTS_URL, { id: project.id, name: project.name, is_active: !project.is_active }, false, null);
        if (res?.status === 'success') { loadProjects(); notifyProjectsChanged(); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Archive this project? All its data will remain but it will be hidden.")) return;
        const res = await remove(PROJECTS_URL, { id }, null);
        if (res?.status === 'success') { ToastMsg("Project archived", "success"); loadProjects(); notifyProjectsChanged(); }
        else ToastMsg(res?.message || "Failed", "error");
    };

    // Member CRUD
    const handleAddMember = async (projectId) => {
        if (!addUserId) return;
        const res = await create(PROJECT_MEMBERS_URL, { project_id: projectId, user_id: addUserId, role: addRole }, false, null);
        if (res?.status === 'success') {
            ToastMsg("Member added", "success");
            setAddUserId(null); setAddRole('member');
            loadMembers(projectId);
        } else {
            ToastMsg(res?.message || "Failed", "error");
        }
    };

    const handleRemoveMember = async (projectId, userId) => {
        const res = await remove(PROJECT_MEMBERS_URL, { project_id: projectId, user_id: userId }, null);
        if (res?.status === 'success') { ToastMsg("Member removed", "success"); loadMembers(projectId); }
    };

    const memberIds = (pid) => (members[pid] || []).map(m => m.user_id);
    const availableUsers = (pid) => users.filter(u => !memberIds(pid).includes(u.id));

    return (
        <ContainerCard>
            <div className="p-4 max-w-2xl">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Projects</h2>

                {/* Organisation-wide project assignment switch */}
                {assignment && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-5 flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">Assign project on customer details</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Shows a Project picker on the add / edit customer form, so a lead can be filed under
                                — or moved to — any project the user belongs to. When off, a lead stays in the project
                                it was created in. Applies to the whole organisation.
                            </p>
                            {assignment.is_enabled && assignment.unassigned_count > 0 && (
                                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                    {assignment.unassigned_count} lead{assignment.unassigned_count === 1 ? '' : 's'} not in any project yet.
                                </p>
                            )}
                        </div>
                        <button type="button" onClick={handleToggleAssignment}
                                disabled={!assignment.can_edit || savingAssignment}
                                title={assignment.can_edit ? '' : 'Only a super admin can change this'}
                                aria-pressed={assignment.is_enabled}
                                className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200 mt-0.5
                                    ${assignment.is_enabled ? 'bg-sidebar-primary' : 'bg-gray-300 dark:bg-gray-600'}
                                    ${!assignment.can_edit || savingAssignment ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200
                                ${assignment.is_enabled ? 'translate-x-4' : 'translate-x-1'}`} />
                        </button>
                    </div>
                )}

                {/* Add project form */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-5 space-y-2">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">New Project</p>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            placeholder="Project name *"
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleAdd()}
                            className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm
                                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                            type="text"
                            placeholder="Description (optional)"
                            value={newDesc}
                            onChange={e => setNewDesc(e.target.value)}
                            className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm
                                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <Button onClick={handleAdd} disabled={adding || !newName.trim()} className="flex items-center gap-1">
                            <Plus className="h-4 w-4" /> Add
                        </Button>
                    </div>
                </div>

                {/* Projects list */}
                {loading ? (
                    <p className="text-sm text-gray-400">Loading…</p>
                ) : (
                    <ul className="space-y-2">
                        {projects.map(project => (
                            <li key={project.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                {/* Project row */}
                                <div className="flex items-center gap-2 px-3 py-2.5">
                                    {editId === project.id ? (
                                        <>
                                            <input autoFocus value={editName} onChange={e => setEditName(e.target.value)}
                                                   onKeyDown={e => { if (e.key === 'Enter') handleUpdate(project.id); if (e.key === 'Escape') setEditId(null); }}
                                                   className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                            <input value={editDesc} onChange={e => setEditDesc(e.target.value)}
                                                   placeholder="Description"
                                                   className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                            <button onClick={() => handleUpdate(project.id)} className="text-green-600 hover:text-green-800"><Check className="h-4 w-4" /></button>
                                            <button onClick={() => setEditId(null)} className="text-gray-500 hover:text-gray-700"><X className="h-4 w-4" /></button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm font-medium ${project.is_active ? 'text-gray-900 dark:text-white' : 'text-gray-400 line-through'}`}>
                                                    {project.name}
                                                </p>
                                                {project.description && (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{project.description}</p>
                                                )}
                                            </div>
                                            <button onClick={() => handleToggleActive(project)}
                                                    className={`text-xs px-1.5 py-0.5 rounded ${project.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700'}`}>
                                                {project.is_active ? 'Active' : 'Inactive'}
                                            </button>
                                            <button onClick={() => { setEditId(project.id); setEditName(project.name); setEditDesc(project.description || ''); }}
                                                    className="text-blue-500 hover:text-blue-700"><Pencil className="h-3.5 w-3.5" /></button>
                                            <button onClick={() => handleDelete(project.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
                                            <button onClick={() => toggleExpand(project.id)}
                                                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded px-1.5 py-0.5">
                                                <Users className="h-3 w-3" />
                                                {expandedProject === project.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* Members panel */}
                                {expandedProject === project.id && (
                                    <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 space-y-2">
                                        {/* Member list */}
                                        {(members[project.id] || []).length === 0 ? (
                                            <p className="text-xs text-gray-400">No members yet.</p>
                                        ) : (
                                            <ul className="space-y-1">
                                                {(members[project.id] || []).map(m => (
                                                    <li key={m.id} className="flex items-center gap-2 text-sm">
                                                        <span className="flex-1 text-gray-700 dark:text-gray-200">{m.name || m.login_name}</span>
                                                        <span className={`text-xs px-1.5 py-0.5 rounded ${m.role === 'admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                                                            {m.role}
                                                        </span>
                                                        <button onClick={() => handleRemoveMember(project.id, m.user_id)} className="text-red-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                        {/* Add member row */}
                                        <div className="flex items-center gap-2 pt-1">
                                            <Select
                                                showSearch
                                                placeholder="Add user..."
                                                value={addUserId}
                                                onChange={setAddUserId}
                                                filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
                                                options={availableUsers(project.id).map(u => ({ value: u.id, label: u.name || u.login_name }))}
                                                className="flex-1"
                                                size="small"
                                            />
                                            <Select
                                                value={addRole}
                                                onChange={setAddRole}
                                                options={[{ value: 'member', label: 'Member' }, { value: 'admin', label: 'Admin' }]}
                                                size="small"
                                                style={{ width: 90 }}
                                            />
                                            <Button onClick={() => handleAddMember(project.id)} disabled={!addUserId} className="text-xs h-7 px-2">
                                                <Plus className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </ContainerCard>
    );
}
